import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "./api";
import { useT, normalizeLang } from "./i18n";
import { tg } from "./telegram";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ThemeSwitcher from "./components/ThemeSwitcher";
import DocumentList from "./pages/DocumentList";
import LettersList from "./pages/LettersList";
import DocumentView from "./pages/DocumentView";
import FilesPage from "./pages/FilesPage";
import FolderView from "./pages/FolderView";
import AdminUsers from "./pages/AdminUsers";
import type { Me } from "./types";

function TabBar({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useT();
  const loc = useLocation();
  const nav = useNavigate();
  const go = (to: string) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    nav(to);
  };
  return (
    <nav className="tabbar">
      <button
        className={"tab" + (loc.pathname === "/" ? " active" : "")}
        onClick={() => go("/")}
      >
        <span className="tab-ico">📄</span>
        <span>{t("tabs.documents")}</span>
      </button>
      <button
        className={"tab" + (loc.pathname.startsWith("/letters") ? " active" : "")}
        onClick={() => go("/letters")}
      >
        <span className="tab-ico">✉️</span>
        <span>{t("tabs.letters")}</span>
      </button>
      <button
        className={"tab" + (loc.pathname.startsWith("/files") ? " active" : "")}
        onClick={() => go("/files")}
      >
        <span className="tab-ico">📁</span>
        <span>{t("tabs.files")}</span>
      </button>
      {isAdmin && (
        <button
          className={"tab" + (loc.pathname.startsWith("/admin") ? " active" : "")}
          onClick={() => go("/admin")}
        >
          <span className="tab-ico">👥</span>
          <span>{t("tabs.users")}</span>
        </button>
      )}
    </nav>
  );
}

export default function App() {
  const { t, setLang } = useT();
  const location = useLocation();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<Me>("/api/me")
      .then((m) => {
        setMe(m);
        setLang(normalizeLang(m.language_code));
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="screen-center">
        <span className="spinner" />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="screen-center">
        <div className="locked card">
          <div className="locked-emoji">🔒</div>
          <h2>{t("access.title")}</h2>
          <p className="hint">{t("access.body")}</p>
          {error && <p className="hint small mono">{error}</p>}
        </div>
      </div>
    );
  }

  const isAdmin = me.role === "admin";
  // top-level screens get the tab bar; drill-in screens (/doc/, /files/:id) use a back button
  const showTabs =
    location.pathname === "/" ||
    location.pathname === "/letters" ||
    location.pathname === "/files" ||
    location.pathname === "/admin";

  return (
    <>
      <div className="corner-controls">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className={showTabs ? "with-tabs" : ""}>
        <Routes>
          <Route path="/" element={<DocumentList />} />
          <Route path="/letters" element={<LettersList />} />
          <Route path="/doc/:slug" element={<DocumentView />} />
          <Route path="/files" element={<FilesPage me={me} />} />
          <Route path="/files/:folderId" element={<FolderView me={me} />} />
          <Route
            path="/admin"
            element={isAdmin ? <AdminUsers me={me} /> : <Navigate to="/" replace />}
          />
        </Routes>
      </div>
      {showTabs && <TabBar isAdmin={isAdmin} />}
    </>
  );
}
